/**
 * Spezialkarten-Definitionen (Single Source of Truth)
 *
 * Diese Datei definiert Spezialkarten, die ins Deck gemischt werden können
 * und besondere Effekte auslösen (z.B. Minispiele).
 *
 * Verwendung:
 * - Server: const { SPECIAL_CARDS } = require('../src/shared/entities/specialCards');
 * - Client: import { SPECIAL_CARDS } from './shared/entities/specialCards';
 */

const rarityModule = require('./rarity');
const { RARITY } = rarityModule;

// =============================================================================
// SPECIAL CARD TYPES
// =============================================================================

/**
 * Typen von Spezialkarten
 */
const SPECIAL_CARD_TYPE = {
  MINIGAME: 'minigame',      // Löst ein Minispiel aus
  INSTANT: 'instant',        // Sofortiger Effekt
  MODIFIER: 'modifier',      // Modifiziert nächsten Stich
};

// =============================================================================
// SPECIAL CARD DEFINITIONS
// =============================================================================

/**
 * Alle verfügbaren Spezialkarten
 *
 * Spezialkarten haben:
 * - id: Eindeutige ID
 * - name: Anzeigename
 * - description: Kurzbeschreibung
 * - detailedDescription: Ausführliche Erklärung
 * - icon: Emoji für UI
 * - type: SPECIAL_CARD_TYPE
 * - rarity: Wie selten erscheint die Karte
 * - enabled: Ist die Karte aktiv?
 * - effect: Effekt-Definition
 * - cardDisplay: Wie die Karte angezeigt wird
 */
const SPECIAL_CARDS = {
  // === MINIGAME TRIGGER CARD ===
  MINIGAME_CARD: {
    id: 'minigameCard',
    name: 'Minispiel-Karte',
    description: 'Startet ein zufälliges Minispiel!',
    detailedDescription: 'Wenn diese Karte gespielt wird, pausiert das Spiel und ALLE Spieler nehmen an einem zufälligen Minispiel teil! Belohnungen werden nach Rang verteilt. Die Karte zählt nicht als normaler Spielzug.',
    icon: '🎮',
    type: SPECIAL_CARD_TYPE.MINIGAME,
    rarity: RARITY.RARE,
    enabled: true,
    effect: {
      type: 'triggerMinigame',
      minigameId: null, // null = zufälliges Minispiel
    },
    cardDisplay: {
      rank: '★',
      suit: 'SPECIAL',
      color: 'purple',
      gradient: ['#9333ea', '#7c3aed', '#6366f1'],
      glow: true,
    },
    messages: {
      play: 'Minispiel-Karte gespielt! Alle Spieler nehmen teil!',
      announce: '🎮 Ein Minispiel startet!',
    },
  },

  // === JOKER WILD CARD (für später) ===
  JOKER_WILD: {
    id: 'jokerWild',
    name: 'Joker-Wildcard',
    description: 'Kann als beliebige Karte gespielt werden!',
    detailedDescription: 'Diese magische Karte kann als JEDE beliebige Karte gespielt werden. Beim Spielen wählst du Rang und Farbe. Perfekt um Straßen oder Flushs zu vervollständigen!',
    icon: '🃏',
    type: SPECIAL_CARD_TYPE.MODIFIER,
    rarity: RARITY.LEGENDARY,
    enabled: false, // Noch nicht implementiert
    effect: {
      type: 'wildcard',
      choosableRank: true,
      choosableSuit: true,
    },
    cardDisplay: {
      rank: '?',
      suit: 'WILD',
      color: 'rainbow',
      gradient: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'],
      glow: true,
      animated: true,
    },
    messages: {
      play: 'Joker-Wildcard! Wähle eine Karte...',
      chosen: '{player} spielt die Wildcard als {card}!',
    },
  },

  // === DOUBLE POINTS CARD (für später) ===
  DOUBLE_POINTS: {
    id: 'doublePoints',
    name: 'Doppel-Punkte',
    description: 'Verdoppelt die Punkte des nächsten Stichs!',
    detailedDescription: 'Der Gewinner des nächsten Stichs erhält DOPPELTE Punkte! Achtung: Gilt auch wenn ein Gegner gewinnt. Strategisch einsetzen!',
    icon: '✨',
    type: SPECIAL_CARD_TYPE.MODIFIER,
    rarity: RARITY.UNCOMMON,
    enabled: false, // Noch nicht implementiert
    effect: {
      type: 'modifier',
      target: 'nextTrick',
      pointsMultiplier: 2,
    },
    cardDisplay: {
      rank: '×2',
      suit: 'BONUS',
      color: 'gold',
      gradient: ['#fbbf24', '#f59e0b', '#d97706'],
      glow: true,
    },
    messages: {
      play: 'Doppel-Punkte aktiv! Nächster Stich ×2!',
      trigger: 'Stich verdoppelt! {points} → {doubledPoints} ⭐',
    },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Hole eine Spezialkarte nach ID
 */
function getSpecialCardById(id) {
  return Object.values(SPECIAL_CARDS).find(c => c.id === id);
}

/**
 * Hole alle aktivierten Spezialkarten
 */
function getEnabledSpecialCards() {
  return Object.values(SPECIAL_CARDS).filter(c => c.enabled);
}

/**
 * Hole Spezialkarten nach Typ
 */
function getSpecialCardsByType(type) {
  return Object.values(SPECIAL_CARDS).filter(c => c.type === type && c.enabled);
}

/**
 * Generiere eine Spezialkarte für das Deck
 * Gibt ein Kartenobjekt zurück, das ins Deck gemischt werden kann
 */
function generateSpecialCardForDeck(specialCardId) {
  const definition = getSpecialCardById(specialCardId);
  if (!definition || !definition.enabled) return null;

  return {
    // Markierung als Spezialkarte
    isSpecial: true,
    specialType: definition.id,

    // Für Kompatibilität mit normalen Karten
    rank: 'SPECIAL',
    suit: 'SPECIAL',

    // Display-Informationen
    display: {
      ...definition.cardDisplay,
      name: definition.name,
      icon: definition.icon,
    },

    // Referenz zur Definition
    definition: definition,
  };
}

/**
 * Generiere Spezialkarten für ein Deck basierend auf Konfiguration
 * @param {number} deckSize - Größe des normalen Decks
 * @param {Object} config - Konfiguration { enabled, probability, maxPerDeck }
 * @returns {Array} Array von Spezialkarten
 */
function generateSpecialCards(deckSize, config) {
  if (!config?.enabled) return [];

  const { probability = 0.05, maxPerDeck = 2 } = config;
  const enabledCards = getEnabledSpecialCards();

  if (enabledCards.length === 0) return [];

  const specialCards = [];
  const targetCount = Math.min(
    maxPerDeck,
    Math.floor(deckSize * probability)
  );

  // Gewichtete Auswahl basierend auf Rarität
  const weights = {
    [RARITY.COMMON]: 50,
    [RARITY.UNCOMMON]: 30,
    [RARITY.RARE]: 15,
    [RARITY.LEGENDARY]: 5,
  };

  const weighted = [];
  for (const card of enabledCards) {
    const weight = weights[card.rarity] || 10;
    for (let i = 0; i < weight; i++) {
      weighted.push(card);
    }
  }

  for (let i = 0; i < targetCount; i++) {
    const index = Math.floor(Math.random() * weighted.length);
    const selected = weighted[index];
    const specialCard = generateSpecialCardForDeck(selected.id);
    if (specialCard) {
      specialCards.push(specialCard);
    }
  }

  return specialCards;
}

/**
 * Prüfe ob eine Karte eine Spezialkarte ist
 */
function isSpecialCard(card) {
  return card?.isSpecial === true;
}

/**
 * Prüfe ob eine Karte eine Minispiel-Karte ist
 */
function isMinigameCard(card) {
  return isSpecialCard(card) && card.specialType === 'minigameCard';
}

// =============================================================================
// EXPORTS
// =============================================================================

module.exports = {
  SPECIAL_CARD_TYPE,
  SPECIAL_CARDS,
  getSpecialCardById,
  getEnabledSpecialCards,
  getSpecialCardsByType,
  generateSpecialCardForDeck,
  generateSpecialCards,
  isSpecialCard,
  isMinigameCard,
};
